import MuiButton, { type ButtonProps as MuiButtonProps } from "@mui/material/Button";
import MuiIconButton from "@mui/material/IconButton";
import { isValidElement, type ElementType, type ReactElement, type ReactNode } from "react";

type AppButtonVariant = "primary" | "secondary" | "outline" | "ghost" | "danger";
type AppButtonSize = "default" | "sm" | "icon";

interface ButtonProps extends Omit<MuiButtonProps, "children" | "color" | "size" | "variant"> {
  asChild?: boolean;
  children: ReactNode;
  size?: AppButtonSize;
  variant?: AppButtonVariant;
}

function variantProps(variant: AppButtonVariant) {
  if (variant === "secondary") return { color: "secondary" as const, variant: "contained" as const };
  if (variant === "outline") return { color: "primary" as const, variant: "outlined" as const };
  if (variant === "ghost") return { color: "inherit" as const, variant: "text" as const };
  if (variant === "danger") return { color: "error" as const, variant: "contained" as const };
  return { color: "primary" as const, variant: "contained" as const };
}

export function Button({ asChild = false, children, className, size = "default", variant = "primary", ...props }: ButtonProps) {
  const mapped = variantProps(variant);
  const child = asChild && isValidElement(children) ? children as ReactElement<Record<string, unknown>> : null;
  const component = child?.type as ElementType | undefined;
  const childProps = child?.props ?? {};
  const classes = ["mui-app-button", className, childProps.className].filter(Boolean).join(" ");
  const content = child ? childProps.children as ReactNode : children;
  const mergedProps = { ...props, ...childProps, className: classes, component };

  if (size === "icon") {
    return <MuiIconButton {...mergedProps} color={mapped.color === "inherit" ? "default" : mapped.color} aria-label={props["aria-label"]}>{content}</MuiIconButton>;
  }

  return <MuiButton {...mergedProps} {...mapped} size={size === "sm" ? "small" : "medium"}>{content}</MuiButton>;
}
